/**
 * 会议管理 - 会议创建
 *
 * @file create.js
 * @author yangwei(yangwei14@baidu.com)
 */

import {Component} from 'san';
import {html} from '../common/raw';

import _ from 'lodash';
import {
    Button,
    Form,
    FormItem,
    TextBox,
    Calendar,
    UserPicker,
    RadioSelect,
    Icon,
    notification,
    Loading
} from '@baiducloud/bce-ui';
import {DurationText, emojiRegExp} from '../common/enums';

import {searchRequester, redirect, getEndTime, getTimeStamp, getCurMinute} from '../common/utils';
import client from '../../client';

export default class extends Component {
    static components = {
        'ui-icon': Icon,
        'ui-button': Button,
        'ui-form': Form,
        'ui-formitem': FormItem,
        'ui-textbox': TextBox,
        'ui-calendar': Calendar,
        'ui-userpicker': UserPicker,
        'ui-radioselect': RadioSelect,
        'ui-loading': Loading
    }
    static filters = {
        partnerText: number => {
            return `${number || 0}位参会人` + '（' + '参会人邮箱将会收到会议邀请' + '）';
        }
    }
    static template = html`
    <template>
        <div class="loading-content" s-if="loading">
            <ui-loading />
        </div>
        <div class="create-content-container">
            <div class="create-content" s-if="!loading">
                <ui-form class="mt15" s-ref="form" formData="{=formData.data=}"
                    descriptor="{{formData.rules}}" errors="{=formData.errors=}">
                    <ui-formitem name="room_name" class="mt20" label="会议主题：" required inline>
                        <ui-textbox
                            max-length="{{100}}"
                            value="{=formData.data.room_name=}"
                            width="{{237}}"
                            placeholder="请输入会议主题" />
                    </ui-formitem>
                    <ui-formitem name="startTime" class="mt20" label="开始时间：" inline>
                        <ui-calendar
                            width="{{257}}"
                            disabled="{{editMode === 'now'}}"
                            value="{=formData.data.startTime.value=}"
                            time
                            format="{{'YYYY-MM-DD HH:mm'}}"
                            layer-width="{{257}}"
                            range="{{formData.data.startTime.range}}"/>
                    </ui-formitem>
                    <ui-formitem name="duration" class="mt20" label="会议时长：" inline>
                        <ui-radioselect
                            datasource="{{formData.data.duration.datasource}}"
                            value="{=formData.data.duration.value=}" />
                    </ui-formitem>
                    <ui-formitem name="partner" class="mt20" label="参会人：" inline >
                        <span class="text-tip">
                            {{formData.data.partner.length | partnerText}}
                        </span>
                        <ui-userpicker
                            placeholder="请输入邮箱"
                            search-requester="{{searchRequester}}"
                            value="{=formData.data.partner=}">
                            <div slot="layer-item">
                                <span>{{item.accountId}}</span>
                            </div>
                        </ui-userpicker>
                    </ui-formitem>
                    <ui-formitem name="site" class="mt20" label="地点：" inline >
                        <ui-textbox
                            value="{=formData.data.site=}"
                            width="{{237}}"
                            placeholder="线上远程" />
                    </ui-formitem>
                    <ui-formitem name="desc" class="mt20" label="描述：" inline >
                        <ui-textbox
                            value="{=formData.data.desc=}"
                            width="{{237}}"
                            placeholder="请输入描述（选填）" />
                    </ui-formitem>
                </ui-form>
            </div>
        </div>
        <div class="create-footer">
            <ui-button
                disabled="{{!formData.data.room_name}}"
                width="{{74}}"
                skin="primary"
                on-click="onSubmit">
                确定
            </ui-button>
            <ui-button
                width="{{74}}"
                on-click="onCancel">
                取消
            </ui-button>
        </div>
</template>
    `;
    initData() {
        return {
            searchRequester,
            loading: false,
            formData: {
                data: {
                    name: '',
                    startTime: {
                        value: getCurMinute(),
                        range: {
                            begin: new Date(2020, 1, 1),
                            end: new Date(3020, 1, 1)
                        }
                    },
                    duration: {
                        value: 30,
                        datasource: DurationText
                    },
                    partner: [],
                    site: '',
                    desc: ''
                },
                rules: {
                    room_name: [ // eslint-disable-line
                        {
                            validator(rule, value, cb) {
                                const errors = [];
                                !value && errors.push(new Error('会议主题不能为空'));
                                value && value.length > 100 && errors.push(new Error('最大长度100'));
                                emojiRegExp.test(value) && errors.push(new Error('会议主题不能包含表情'));
                                cb(errors);
                            }
                        }
                    ],
                    partner: [{
                        validator(rule, value, cb) {
                            const errors = [];
                            value && value.length >= 30 && errors.push(new Error('暂时仅支持填写最多30个邮箱地址'));
                            cb(errors);
                        }
                    }],
                    site: [{min: 0, max: 30, message: '最大长度30'}],
                    desc: [{min: 0, max: 100, message: '最大长度100'}]
                },
                errors: {}
            }
        };
    }
    attached() {
        const query = this.data.get('route.query');
        if (query && query.mode) {
            this.data.set('editMode', query.mode);
            query.mode === 'modify' && this.getData(query.id);
        }
    }
    async getData(id) {
        this.data.set('loading', true);
        const data = await client.getMeetingList();
        let {startTime, duration} = this.data.get('formData.data');
        const arr = _.filter(data.data.rows, item => item.room_id === Number(id))[0];
        let partner = arr.room_partner;
        partner.length && (partner = _.map(partner, item => ({
            accountId: item.username,
            username: item.username.split('@')[0]
        })));
        arr && this.data.set('formData.data', {
            room_name: arr.room_name, //eslint-disable-line
            startTime: _.extend(startTime, {
                value: arr.room_stime * 1000
            }),
            duration: _.extend(duration, {
                value: arr.room_dtime
            }),
            partner: partner,
            site: arr.room_site,
            desc: arr.room_desc
        });
        this.data.set('loading', false);
    }
    onCancel() {
        redirect('#/meeting/list');
    }
    async onSubmit() {
        await this.ref('form').validateForm();
        const editMode = this.data.get('editMode');
        /*eslint-disable*/
        let {room_name, desc, startTime, duration, partner, site} = this.data.get('formData.data');
        partner.length && (partner = _.map(partner, item => ({
            username: item.accountId,
            name: item.accountId,
            email: item.accountId
        })));
        let payload = {
            room_name,
            room_desc: desc,
            room_stime: getTimeStamp(startTime.value),
            room_etime: getTimeStamp(getEndTime(startTime.value, duration.value)),
            room_dtime: duration.value,
            room_partner: partner,
            room_owner: {
                username: '',
            },
            room_site: site,
        }
        if (editMode === 'modify') {
            const data = await client.modifyMeeting(this.data.get('route.query.id'),
                _.extend(payload, {
                    update_actions: 0
                }));
            if (data.status === 0) {
                redirect(`#/meeting/success?id=${data.data.room_id}`)
            }
            else {
                notification.error(data.message);
            }
        }
        else {
            let type = 1;
            //没有参会者 或者立即开会 
            (partner.length === 0 || editMode === 'now') && (type = 0);
            const data = await client.scheduleMeeting(_.extend(payload, {
                room_type: type // 0:快速会议，1:预约会议
            }));
            if (data.status === 0) {
                // type === 0 && window.open(data.data.room_url);
                redirect(`#/meeting/success?id=${data.data.room_id}`)
            }
            else {
                notification.error(data.message);
            }
        }
        /*eslint-enable*/
    }
}
